<template>
  <div class="tree-menu">
    <div class="tree-header">
      <ShareButton
        :projectId="projectId"
        :initialIsPublic="isPublic"
        :isOwner="isOwner"
        @toast="$emit('toast', $event)"
      />
    </div>
    <Divider />
    <div class="tree-content">
      <!-- Recreate Outputs Section - only in dev mode when complete -->
      <div v-if="devMode && !isProcessing && !isPending && frameworks.length > 0" class="recreate-section">
        <div v-if="!recreateMode" class="recreate-trigger">
          <Button variant="raw" class="recreate-button" @click="startRecreateMode">
            <QIcon :path="mdiRefresh" :size="16" />
            <span>Recreate Outputs...</span>
          </Button>
        </div>

        <div v-else class="recreate-controls">
          <div class="recreate-header">Select outputs to recreate:</div>
          <select v-model="recreateModel" class="recreate-model-select">
            <option value="">Same models as last run</option>
            <option value="production">Production models</option>
            <option value="anthropic/claude-haiku-4.5">Cheap test model</option>
          </select>
          <div class="recreate-actions">
            <button class="cancel-btn" @click="cancelRecreateMode">Cancel</button>
            <button class="cancel-btn" @click="selectAllOutputs">All</button>
            <button
              class="proceed-btn"
              :disabled="selectedForRecreation.size === 0 || isRecreating"
              @click="proceedWithRecreation"
            >
              {{ isRecreating ? 'Starting...' : 'Proceed' }}
            </button>
          </div>
        </div>
      </div>
      <Divider v-if="devMode && !isProcessing && !isPending && frameworks.length > 0" />

      <div v-if="isProcessing" class="nav-item">
        <router-link
          :to="`/edit/${projectId}/`"
          class="nav-item-header special-item"
          :class="{ selected: !selectedItem }"
        >
          <span class="spinning">
            <QIcon :path="mdiLoading" :size="16" />
          </span>
          <span>Currently Editing ({{ getOverallProgress() }}%)</span>
          <div v-if="devMode" class="stop-button" title="Force stop processing" @click.prevent.stop="handleForceStop">
            <QIcon :path="mdiClose" :size="14" />
          </div>
        </router-link>
      </div>
      <Divider v-if="isProcessing" />

      <div v-for="framework in frameworks" :key="framework.frameworkId" class="nav-item">
        <template v-if="hasOutputs(framework)">
          <Button variant="raw" class="nav-item-header header" @click="toggleNavItem(framework.frameworkId)">
            <QIcon
              :path="mdiChevronRight"
              :size="16"
              class="chevron"
              :class="{ expanded: expandedFrameworks.has(framework.frameworkId) }"
            />
            <span class="framework-name">{{ framework.name }}</span>
            <span class="output-count">
              ({{ devMode ? `V${framework.version ?? 1} - ` : ''
              }}{{ getFrameworkOutputs(framework).length }} outputs)</span
            >
          </Button>

          <Transition name="slide">
            <div v-if="expandedFrameworks.has(framework.frameworkId)" class="nav-children">
              <template v-if="isProcessing || isPending">
                <!-- Show streaming state for files during processing -->
                <div v-for="output in getFrameworkOutputs(framework)" :key="output.name" class="nav-item">
                  <div
                    v-if="output.status === 'pending'"
                    class="nav-item-header"
                    :class="{
                      selected: selectedItem === `${framework.frameworkId}:${output.name}`,
                      streaming: output.status === 'started',
                      pending: output.status === 'pending',
                    }"
                  >
                    <div v-if="output.status === 'started'" class="spinning">
                      <QIcon :path="mdiLoading" :size="16" />
                    </div>
                    <QIcon v-else-if="output.status === 'complete'" :path="getFileIcon(output.name)" :size="16" />
                    <QIcon v-else :path="getFileIcon(output.name)" :size="16" color="var(--color-text-muted)" />
                    <span class="output-name">{{ humanizeFileName(output.name) }}</span>
                    <div
                      v-if="output.status === 'complete'"
                      class="nav-item-action-button"
                      @click.prevent.stop="handlePrint(framework.frameworkId, output.name)"
                      title="Print"
                    >
                      <QIcon :path="mdiPrinter" :size="16" />
                    </div>
                    <div
                      v-if="output.status === 'complete'"
                      class="nav-item-action-button"
                      @click.prevent.stop="handleDownload(framework.frameworkId, output.name)"
                      title="Download"
                    >
                      <QIcon :path="mdiDownload" :size="16" />
                    </div>
                  </div>
                  <router-link
                    v-else
                    :to="getOutputUrl(framework.frameworkId, output.name)"
                    class="nav-item-header"
                    :class="{
                      selected: selectedItem === `${framework.frameworkId}:${output.name}`,
                      streaming: output.status === 'started',
                      pending: output.status === 'pending',
                    }"
                  >
                    <div v-if="output.status === 'started'" class="spinning">
                      <QIcon :path="mdiLoading" :size="16" />
                    </div>
                    <QIcon v-else-if="output.status === 'complete'" :path="getFileIcon(output.name)" :size="16" />
                    <QIcon v-else :path="getFileIcon(output.name)" :size="16" color="var(--color-text-muted)" />
                    <span class="output-name">{{ humanizeFileName(output.name) }}</span>
                    <div
                      v-if="output.status === 'complete'"
                      class="nav-item-action-button"
                      @click.prevent.stop="handlePrint(framework.frameworkId, output.name)"
                      title="Print"
                    >
                      <QIcon :path="mdiPrinter" :size="16" />
                    </div>
                    <div
                      v-if="output.status === 'complete'"
                      class="nav-item-action-button"
                      @click.prevent.stop="handleDownload(framework.frameworkId, output.name)"
                      title="Download"
                    >
                      <QIcon :path="mdiDownload" :size="16" />
                    </div>
                  </router-link>
                </div>
              </template>
              <template v-else>
                <!-- Show regular outputs when processing is complete -->
                <div
                  v-for="output in framework.outputs.filter((o: any) => !o.name.endsWith('_highlights'))"
                  :key="output.name"
                  class="nav-item"
                >
                  <div v-if="recreateMode" class="nav-item-header recreate-item">
                    <input
                      type="checkbox"
                      class="recreate-checkbox"
                      :checked="selectedForRecreation.has(output.name)"
                      @change="toggleOutputSelection(output.name)"
                    />
                    <QIcon :path="getFileIcon(output.name)" :size="16" />
                    <span class="output-name">{{ humanizeFileName(output.name) }}</span>
                  </div>
                  <router-link
                    v-else
                    :to="getOutputUrl(framework.frameworkId, output.name)"
                    class="nav-item-header"
                    :class="{ selected: selectedItem === `${framework.frameworkId}:${output.name}` }"
                  >
                    <QIcon :path="getFileIcon(output.name)" :size="16" />
                    <span class="output-name">{{ humanizeFileName(output.name) }}</span>
                    <div
                      v-if="!output.status || output.status === 'complete'"
                      class="nav-item-action-button"
                      @click.prevent.stop="handlePrint(framework.frameworkId, output.name)"
                      title="Print"
                    >
                      <QIcon :path="mdiPrinter" :size="16" />
                    </div>
                    <div
                      v-if="!output.status || output.status === 'complete'"
                      class="nav-item-action-button"
                      @click.prevent.stop="handleDownload(framework.frameworkId, output.name)"
                      title="Download"
                    >
                      <QIcon :path="mdiDownload" :size="16" />
                    </div>
                  </router-link>
                </div>
              </template>

              <div
                v-if="framework.intermediateOutputs && framework.intermediateOutputs.length > 0"
                class="nav-item intermediate"
              >
                <Button
                  variant="raw"
                  class="nav-item-header header"
                  @click="toggleNavItem(`${framework.frameworkId}_intermediate`)"
                >
                  <QIcon
                    :path="mdiChevronRight"
                    :size="16"
                    class="chevron"
                    :class="{ expanded: expandedFrameworks.has(`${framework.frameworkId}_intermediate`) }"
                  />
                  <span class="framework-name">Intermediate Analysis</span>
                  <span class="output-count"> ({{ framework.intermediateOutputs.length }} outputs)</span>
                </Button>

                <div v-if="expandedFrameworks.has(`${framework.frameworkId}_intermediate`)" class="nav-children">
                  <div v-for="output in framework.intermediateOutputs" :key="output.name" class="nav-item">
                    <div v-if="recreateMode" class="nav-item-header recreate-item">
                      <input
                        type="checkbox"
                        class="recreate-checkbox"
                        :checked="selectedForRecreation.has(output.name)"
                        @change="toggleOutputSelection(output.name)"
                      />
                      <QIcon :path="getFileIcon(output.name)" :size="14" />
                      <span class="output-name">{{ humanizeFileName(output.name) }}</span>
                    </div>
                    <router-link
                      v-else
                      :to="`/edit/${projectId}/${encodeURIComponent(output.name)}`"
                      class="nav-item-header"
                      :class="{ selected: selectedItem === `${framework.frameworkId}:${output.name}` }"
                    >
                      <QIcon :path="getFileIcon(output.name)" :size="14" />
                      <span class="output-name">{{ humanizeFileName(output.name) }}</span>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </Transition>
        </template>
      </div>

      <!-- Special Documents Section -->
      <div v-if="manuscript || costs" class="nav-item intermediate special">
        <Button variant="raw" class="nav-item-header header" @click="toggleNavItem('special_documents')">
          <QIcon
            :path="mdiChevronRight"
            :size="16"
            class="chevron"
            :class="{ expanded: expandedFrameworks.has('special_documents') }"
          />
          <span class="framework-name">Additional Documents</span>
          <span class="file-count"> ({{ (manuscript ? 1 : 0) + (costs ? 1 : 0) }} files)</span>
        </Button>

        <div v-if="expandedFrameworks.has('special_documents')" class="nav-children">
          <div v-if="manuscript" class="nav-item">
            <router-link
              :to="`/edit/${projectId}/system/manuscript`"
              class="nav-item-header"
              :class="{ selected: selectedItem === 'manuscript' }"
            >
              <QIcon :path="getFileIcon('manuscript')" :size="14" />
              <span class="file-name">{{ humanizeFileName('manuscript') }}</span>
            </router-link>
          </div>

          <div v-if="costs" class="nav-item">
            <router-link
              :to="`/edit/${projectId}/system/costs`"
              class="nav-item-header"
              :class="{ selected: selectedItem === 'costs' }"
            >
              <QIcon :path="getFileIcon('costs')" :size="14" />
              <span class="file-name">{{ humanizeFileName('costs') }}</span>
            </router-link>
          </div>
        </div>
      </div>

      <div v-if="frameworks.length === 0" class="empty-state">
        <QIcon :path="mdiFileDocumentOutline" :size="24" color="var(--color-text-muted)" />
        <p>No deliverables available yet.</p>
        <p class="empty-subtitle">Your completed edits will appear here.</p>
      </div>
    </div>

    <div v-if="isChapterRoast && !props.isProcessing && !props.isPending" class="upsell-cta">
      <p class="upsell-headline">Ready to go deeper?</p>
      <p class="upsell-body">
        You just saw what Twig can find in one chapter. The full edit does that to your whole manuscript — editorial
        letter, chapter notes, revision plan. At a fraction of what a traditional edit costs.
      </p>
      <router-link to="/credits?promo=ROAST20" class="upsell-button">Save $119 on 3 edits</router-link>
      <a :href="singleEditUrl" class="upsell-single">or get a single edit</a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Button, Divider } from '@dabble/quillui';
import {
  mdiChevronRight,
  mdiClose,
  mdiDownload,
  mdiFileDocumentOutline,
  mdiLoading,
  mdiPrinter,
  mdiRefresh,
} from '@mdi/js';
import { computed, ref, watch, withDefaults } from 'vue';

const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const singleEditUrl = isDev ? '/' : 'https://twig.io/dev-edit?code=ROAST20';
import { getFileIcon, humanizeFileName } from '../lib/helpers';
import { buildOutputUrl } from '../lib/urlUtils';
import { recreateOutputs, resetProject } from '../services/api';
import { useAuthStore } from '../stores/auth';
import type { FrameworkStatus } from '../types';
import QIcon from './QIcon.vue';
import ShareButton from './ShareButton.vue';

const authStore = useAuthStore();

interface Props {
  frameworks: any[];
  selectedItem: string | null;
  projectId: string;
  manuscript?: string | undefined;
  costs?: any | undefined;
  isPending?: boolean;
  isProcessing?: boolean;
  isPublic?: boolean;
  isOwner?: boolean;
  devMode?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isProcessing: false,
  isPublic: false,
  isOwner: false,
  devMode: false,
});

const emit = defineEmits<{
  (e: 'toast', message: string): void;
  (e: 'recreate-started'): void;
  (e: 'force-stopped'): void;
}>();

const isChapterRoast = computed(() => props.frameworks.some((f: any) => f.frameworkId === 'chapter-roast'));

// Recreate mode state
const recreateMode = ref(false);
const selectedForRecreation = ref(new Set<string>());
const isRecreating = ref(false);
const recreateModel = ref('');

const expandedFrameworks = ref(new Set<string>());

// Generate nested URL for output
function getOutputUrl(frameworkId: string, outputName: string): string {
  return buildOutputUrl(props.projectId, frameworkId, outputName);
}

// Session storage key for this project's expanded state
const storageKey = `treemenu_expanded_${props.projectId}`;

// Load expanded state from session storage
function loadExpandedState(): Set<string> {
  try {
    const stored = sessionStorage.getItem(storageKey);
    if (stored) {
      return new Set(JSON.parse(stored));
    }
  } catch (e) {
    console.warn('Failed to load expanded state from session storage:', e);
  }
  return new Set();
}

// Save expanded state to session storage
function saveExpandedState(expanded: Set<string>) {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(Array.from(expanded)));
  } catch (e) {
    console.warn('Failed to save expanded state to session storage:', e);
  }
}

function getOverallProgress() {
  if (!props.frameworks || props.frameworks.length === 0) return 0;

  // Calculate total and completed files
  let totalSteps = 0;
  let completedSteps = 0;

  for (const framework of props.frameworks) {
    totalSteps += framework.totalSteps;
    completedSteps += framework.completedSteps;
  }

  return totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
}

// Initialize frameworks as expanded when they are first loaded
watch(
  () => props.frameworks.length,
  newLength => {
    if (newLength > 0) {
      const storedExpanded = loadExpandedState();
      const newExpandedSet = new Set<string>();

      props.frameworks.forEach((framework: FrameworkStatus) => {
        // If we have stored state, use it; otherwise default to expanded
        if (storedExpanded.size > 0) {
          if (storedExpanded.has(framework.frameworkId)) {
            newExpandedSet.add(framework.frameworkId);
          }
        } else {
          // Default to expanded for new projects
          newExpandedSet.add(framework.frameworkId);
        }
      });

      expandedFrameworks.value = newExpandedSet;
      saveExpandedState(newExpandedSet);
    }
  },
  { immediate: true }
);

function toggleNavItem(frameworkId: string) {
  if (expandedFrameworks.value.has(frameworkId)) {
    expandedFrameworks.value.delete(frameworkId);
  } else {
    expandedFrameworks.value.add(frameworkId);
  }
  expandedFrameworks.value = new Set(expandedFrameworks.value);
  saveExpandedState(expandedFrameworks.value);
}

function getFrameworkOutputs(framework: any) {
  if (Array.isArray(framework.outputs)) {
    return framework.outputs
      .filter((output: any) => !output.name.endsWith('_highlights'))
      .map((output: any) => ({
        name: output.name,
        status: output.status || 'complete',
        content: output.content || '',
      }));
  }
  return [];
}

// Check if a framework has any outputs (expected or completed)
function hasOutputs(framework: any): boolean {
  return framework.outputs && framework.outputs.length > 0;
}

async function handleDownload(frameworkId: string, fileName: string) {
  try {
    // Get auth token and append as query parameter for direct download
    const token = await authStore.getIdToken();
    const downloadUrl = token
      ? `/api/projects/${props.projectId}/download/${frameworkId}/${fileName}?token=${encodeURIComponent(token)}`
      : `/api/projects/${props.projectId}/download/${frameworkId}/${fileName}`;

    const response = await fetch(downloadUrl);

    if (!response.ok) {
      throw new Error('Download failed');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = `${humanizeFileName(fileName)}.docx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    console.error('Download error:', error);
    // TODO: Show user-friendly error message
  }
}

// Recreate mode functions
function startRecreateMode() {
  recreateMode.value = true;
  selectedForRecreation.value = new Set();
}

function cancelRecreateMode() {
  recreateMode.value = false;
  selectedForRecreation.value = new Set();
}

function selectAllOutputs() {
  const all = new Set<string>();
  for (const framework of props.frameworks) {
    for (const output of framework.outputs) {
      all.add(output.name);
    }
  }
  selectedForRecreation.value = all;
}

function toggleOutputSelection(outputName: string) {
  if (selectedForRecreation.value.has(outputName)) {
    selectedForRecreation.value.delete(outputName);
  } else {
    selectedForRecreation.value.add(outputName);
  }
  // Trigger reactivity
  selectedForRecreation.value = new Set(selectedForRecreation.value);
}

async function proceedWithRecreation() {
  if (selectedForRecreation.value.size === 0) return;

  isRecreating.value = true;
  try {
    const outputs = Array.from(selectedForRecreation.value);
    const model = recreateModel.value || undefined;

    // Dry run first: show exactly which steps will run (missing inputs pull
    // in upstream steps, so this can be more than what was selected)
    const plan = await recreateOutputs(props.projectId, outputs, model, true);
    const selected = plan.filter(s => !s.cascaded);
    const cascaded = plan.filter(s => s.cascaded);
    let message = `This will re-run ${plan.length} step${plan.length === 1 ? '' : 's'}:\n\n`;
    message += selected.map(s => `• ${s.name}`).join('\n');
    if (cascaded.length > 0) {
      message += `\n\nPlus ${cascaded.length} more pulled in because their outputs are needed and missing:\n`;
      message += cascaded.map(s => `• ${s.name}`).join('\n');
    }
    message += '\n\nProceed?';
    if (!confirm(message)) return;

    await recreateOutputs(props.projectId, outputs, model);

    // Reset state and notify parent
    recreateMode.value = false;
    selectedForRecreation.value = new Set();
    emit('recreate-started');
  } catch (error: any) {
    console.error('Recreation failed:', error);
    emit('toast', error.message || 'Failed to start recreation');
  } finally {
    isRecreating.value = false;
  }
}

async function handleForceStop() {
  if (!confirm('Force stop processing? You can re-trigger recreate after.')) return;

  try {
    await resetProject(props.projectId);
    emit('force-stopped');
  } catch (error: any) {
    console.error('Force stop failed:', error);
    emit('toast', error.message || 'Failed to stop processing');
  }
}

// Simple markdown to HTML converter
function markdownToHtml(markdown: string): string {
  let html = markdown;

  // Headers
  html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
  html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');

  // Bold
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // Italic
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');

  // Line breaks (convert double newlines to paragraphs)
  html = html
    .split('\n\n')
    .map(para => `<p>${para.replace(/\n/g, '<br>')}</p>`)
    .join('');

  return html;
}

async function handlePrint(frameworkId: string, fileName: string) {
  // Open window immediately to avoid popup blocker
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Pop-up blocked. Please allow pop-ups to print.');
    return;
  }

  // Show loading state
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Loading...</title>
        <style>
          body {
            font-family: 'PT Serif', Georgia, serif;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100vh;
            margin: 0;
            color: #666;
          }
        </style>
      </head>
      <body>
        <div>Loading content for printing...</div>
      </body>
    </html>
  `);

  try {
    const token = await authStore.getIdToken();

    // Fetch project title from status endpoint
    const statusUrl = token
      ? `/api/projects/${props.projectId}/status?token=${encodeURIComponent(token)}`
      : `/api/projects/${props.projectId}/status`;

    const statusResponse = await fetch(statusUrl);
    let projectTitle = '';
    if (statusResponse.ok) {
      const statusData = await statusResponse.json();
      projectTitle = statusData.projectTitle || '';
    }

    // Fetch file content
    const fileUrl = token
      ? `/api/projects/${props.projectId}/files/${frameworkId}/${fileName}?token=${encodeURIComponent(token)}`
      : `/api/projects/${props.projectId}/files/${frameworkId}/${fileName}`;

    const response = await fetch(fileUrl);

    if (!response.ok) {
      throw new Error('Failed to load content for printing');
    }

    const data = await response.json();
    const content = markdownToHtml(data.content);

    // Twig.io logo SVG
    const logoSvg = `<svg width="131" height="65" viewBox="0 0 262 129" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clip-path="url(#clip0_2_21)">
        <path d="M38.5653 98.715C36.6403 99.3309 34.2931 99.6398 31.522 99.6398C24.9008 99.6398 20.0711 97.9637 17.0311 94.6115C13.9893 91.2594 12.4703 86.0776 12.4703 79.0645V45.6586H0V40.6882L22.5157 14.7957H27.4807V40.3414H48.2643V45.6586H27.4807V79.9892C27.4807 86.0776 30.4827 89.121 36.4869 89.121C38.1034 89.121 39.6424 88.7941 41.1055 88.1385C42.5668 87.4846 44.454 86.3089 46.7633 84.6129L50.2272 89.4677L38.5653 98.715Z" fill="#1E1E1E"/>
        <path d="M152.875 41.1506L126.896 98.5995H116.966L105.304 70.0484L90.8706 98.5995H80.9406L63.0435 55.0215L52.1899 48.664V44.5027L73.089 34.7931L77.4767 52.2473L91.3325 85.7689L102.302 62.535L99.1841 54.9059L88.3304 48.5484V44.3871L109.23 34.6775L113.617 52.1318L127.127 84.8441L140.636 53.7501L124.355 42.0753L126.896 36.8737L152.875 36.9893L152.875 41.1506Z" fill="#1E1E1E"/>
        <path d="M191.326 98.4839H158.303V94.3226L168.579 86.1156V53.75L158.303 46.699V42.5377L178.624 34.6774H183.589V89.4677L193.981 93.2823L191.326 98.4839ZM181.857 2.71639C183.474 4.52794 184.282 6.74405 184.282 9.3629C184.282 11.2124 183.705 12.9661 182.55 14.6223C181.396 16.2804 179.932 17.5898 178.163 18.5524C176.391 19.5169 174.659 19.9973 172.967 19.9973C170.502 19.9973 168.463 19.1123 166.847 17.3387C165.23 15.5669 164.422 13.3327 164.422 10.6344C164.422 8.78493 165 7.033 166.154 5.37497C167.309 3.71878 168.77 2.40933 170.542 1.44487C172.312 0.482232 174.006 -3.05176e-05 175.622 -3.05176e-05C178.163 -3.05176e-05 180.241 0.906657 181.857 2.71639Z" fill="#1E1E1E"/>
        <path d="M248.606 45.7742C250.684 48.8572 251.724 52.1317 251.724 55.5995C251.724 60.6096 249.683 65.367 245.604 69.875C241.523 74.3831 236.558 77.6016 230.709 79.5269C228.706 81.6852 227.707 83.9193 227.707 86.2312C227.707 87.4647 228.631 88.6008 230.478 89.6411C232.325 90.6814 235.288 92.0505 239.369 93.7446C244.756 95.9029 248.953 97.9059 251.955 99.7554C254.957 101.605 256.458 103.839 256.458 106.46C256.458 108.847 254.957 111.853 251.955 115.476C248.952 119.097 245.238 122.256 240.812 124.954C236.385 127.651 232.132 129 228.053 129C223.588 129 218.372 128.267 212.408 126.804C206.442 125.339 201.457 123.874 197.455 122.411V104.032H202.42C206.653 109.349 210.925 113.375 215.237 116.112C219.547 118.846 224.434 120.215 229.901 120.215C231.979 120.215 233.845 119.829 235.501 119.059C237.155 118.288 238.445 117.305 239.369 116.112C240.293 114.916 240.754 113.78 240.754 112.702C240.754 110.697 239.58 108.963 237.233 107.5C234.884 106.035 231.286 104.224 226.437 102.067C221.741 100.062 218.199 98.3105 215.814 96.8078C213.427 95.3051 212.235 93.5911 212.235 91.664C212.235 89.8145 213.485 87.9271 215.987 86C218.488 84.0746 221.472 82.3788 224.936 80.914C223.242 81.0693 221.972 81.1452 221.125 81.1452C216.969 81.1452 213.139 80.2403 209.637 78.4288C206.133 76.619 203.362 74.1916 201.323 71.1465C199.283 68.1032 198.263 64.8088 198.263 61.2634C198.263 57.0263 199.764 52.9228 202.767 48.9529C205.769 44.9849 209.655 41.7862 214.428 39.3588C219.2 36.9314 224.05 35.7177 228.977 35.7177C234.595 35.7177 239.484 37.2601 243.641 40.3413H262L261.769 45.7741L248.606 45.7742ZM235.039 68.4301C236.771 66.8118 237.637 64.3844 237.637 61.1479C237.637 56.6795 236.212 53.0962 233.365 50.3979C230.516 47.7013 226.744 46.3521 222.049 46.3521C215.428 46.3521 212.119 49.4731 212.119 55.7151C212.119 60.2628 213.543 63.9221 216.391 66.6962C219.238 69.4704 222.973 70.8575 227.591 70.8575C230.824 70.8575 233.307 70.0484 235.039 68.4301Z" fill="#1E1E1E"/>
      </g>
      <defs>
        <clipPath id="clip0_2_21">
          <rect width="262" height="129" fill="white"/>
        </clipPath>
      </defs>
    </svg>`;

    // Replace loading content with actual content
    printWindow.document.open();
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${humanizeFileName(fileName)}${projectTitle ? ' for ' + projectTitle : ''}</title>
          <link rel="preconnect" href="https://fonts.googleapis.com">
          <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
          <link href="https://fonts.googleapis.com/css2?family=PT+Serif:ital,wght@0,400;0,700;1,400;1,700&display=swap" rel="stylesheet">
          <style>
            @page {
              margin: 0.75in;
            }

            body {
              font-family: 'PT Serif', Georgia, serif;
              line-height: 1.8;
              max-width: 8.5in;
              margin: 0 auto;
              padding: 0;
              color: #1a1a1a;
            }

            .header {
              text-align: center;
              padding: 20px 0 30px 0;
              border-bottom: 1px solid #333;
              margin-bottom: 40px;
            }

            .header .logo {
              margin-bottom: 15px;
            }

            .header .logo svg {
              width: 100px;
              height: auto;
            }

            .header h1 {
              margin: 0 0 10px 0;
              font-size: 24px;
              color: #1a1a1a;
              font-weight: 700;
              letter-spacing: -0.5px;
            }

            .header .subtitle {
              margin: 0;
              font-size: 14px;
              color: #666;
              font-style: italic;
            }

            .content {
              margin-bottom: 40px;
            }

            .content h1 {
              font-size: 22px;
              margin-top: 1.5em;
              margin-bottom: 0.5em;
              page-break-after: avoid;
              color: #1a1a1a;
            }

            .content h2 {
              font-size: 18px;
              margin-top: 1.5em;
              margin-bottom: 0.5em;
              page-break-after: avoid;
              color: #1a1a1a;
            }

            .content h3 {
              font-size: 16px;
              margin-top: 1.5em;
              margin-bottom: 0.5em;
              page-break-after: avoid;
              color: #1a1a1a;
            }

            .content p {
              margin: 1em 0;
              line-height: 1.8;
            }

            .content strong {
              font-weight: 700;
            }

            .content em {
              font-style: italic;
            }

            .footer {
              text-align: center;
              padding: 20px 0;
              border-top: 1px solid #ddd;
              margin-top: 40px;
              font-size: 11px;
              color: #666;
            }

            .footer .logo {
              margin-bottom: 5px;
            }

            .footer .logo svg {
              width: 60px;
              height: auto;
            }

            .footer .message {
              margin-bottom: 3px;
              font-style: italic;
            }

            .footer .url {
              color: #333;
              text-decoration: none;
            }

            @media print {
              @page {
                margin: 0.75in;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">${logoSvg}</div>
            <h1>${humanizeFileName(fileName)}${projectTitle ? ' for ' + projectTitle : ''}</h1>
            <div class="subtitle">Developmental Edit • Twig.io</div>
          </div>
          <div class="content">
            ${content}
          </div>
          <div class="footer">
            <div class="logo">${logoSvg}</div>
            <div class="message">Thanks for working with us. Happy writing! ✨</div>
            <div class="url">twig.io</div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();

    // Trigger print dialog
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  } catch (error) {
    console.error('Print error:', error);
    printWindow.document.open();
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Error</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
              display: flex;
              align-items: center;
              justify-content: center;
              height: 100vh;
              margin: 0;
              color: #d32f2f;
            }
          </style>
        </head>
        <body>
          <div>Failed to load content. Please try downloading instead.</div>
        </body>
      </html>
    `);
    printWindow.document.close();
  }
}
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: all 0.15s ease;
  overflow: hidden;
}

.slide-enter-from,
.slide-leave-to {
  height: 0;
  opacity: 0;
}

.tree-menu {
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}

.tree-menu::after {
  content: '';
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 1px;
  background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.04) 50%, transparent 100%);
  pointer-events: none;
}

.tree-content {
  flex: 1;
  overflow-y: auto;
  padding: 12px 8px;
  position: relative;
  z-index: 1;
}

.nav-item-header.special-item {
  font-weight: 500;
  color: var(--color-accent);
}

.nav-item-header.special-item:hover {
  background: var(--color-bg-hover);
}

.nav-item-header.special-item.selected {
  background: var(--color-accent);
  color: white;
}

.spinning {
  display: inline-flex;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.nav-item {
  display: flex;
  flex-direction: column;
}

.nav-item-header {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 4px 4px 12px;
  background: transparent;
  border: none !important;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--color-text-secondary);
  margin: 1px 6px;
  transition: all var(--transition-fast);
  text-decoration: none;
  box-shadow: none !important;
  transform: none !important;
}

.nav-item-header.header {
  margin-top: 12px;
  margin-bottom: 2px;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--color-text-muted);
  font-weight: 500;
  padding-left: 8px;
  box-shadow: none !important;
  background: transparent !important;
  border: none !important;
  border-radius: 6px;
  transform: none !important;
}

.nav-item-header.header:hover {
  background: rgba(0, 0, 0, 0.02) !important;
}

.chevron {
  transition: transform 0.15s ease;
  color: var(--color-text-muted);
  flex-shrink: 0;
}

.chevron.expanded {
  transform: rotate(90deg);
}

.nav-children .nav-item-header.header {
  margin-top: 0;
}

.nav-item.intermediate .nav-item-header {
  opacity: 0.75;
}

.nav-item-header:hover {
  background: rgba(0, 0, 0, 0.03) !important;
  color: var(--color-text-primary);
  box-shadow: none !important;
  transform: none !important;
}

.nav-item-header.selected {
  background: var(--color-accent) !important;
  color: white;
  font-weight: 500;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12) !important;
}

.nav-item-header.pending {
  opacity: 0.5;
  cursor: not-allowed;
}

.nav-item-header.streaming {
  background: linear-gradient(90deg, transparent 0%, var(--color-accent-light) 50%, transparent 100%);
  background-size: 200% 100%;
  animation: streaming 2s linear infinite;
}

.nav-item-header.streaming.selected {
  background: linear-gradient(
    90deg,
    var(--color-accent) 0%,
    var(--color-accent-dark, #4a90e2) 50%,
    var(--color-accent) 100%
  );
  background-size: 200% 100%;
  animation: streaming 2s linear infinite;
  color: white;
}

@keyframes streaming {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.spinning :deep(svg) {
  animation: spin 1s linear infinite;
}

.nav-item-action-button {
  display: flex;
  padding: 4px;
  border-radius: 6px;
  margin: -2px;
  opacity: 0;
  transition: all var(--transition-fast);
  color: var(--color-text-muted);
}
.nav-item-action-button:hover {
  background: rgba(0, 0, 0, 0.05);
  color: var(--color-accent);
}
.nav-item-header:hover .nav-item-action-button {
  opacity: 1;
}
.nav-item-header.selected .nav-item-action-button {
  color: white;
}
.nav-item-header.selected .nav-item-action-button:hover {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.framework-name {
  font-weight: 500;
  flex: 1;
}

.output-count {
  font-size: 11px;
  color: var(--color-text-muted);
  font-weight: 400;
  opacity: 0.6;
}

.nav-children .nav-item-header {
  padding-left: 24px;
}

.nav-children .nav-children .nav-item-header {
  padding-left: 36px;
}

.output-name,
.header {
  flex: 1;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-2xl) var(--spacing-xl);
  text-align: center;
  color: var(--color-text-muted);
}

.empty-state p {
  margin: var(--spacing-sm) 0 0 0;
  font-size: 13px;
  line-height: 1.6;
}

.empty-subtitle {
  font-size: 12px !important;
  color: var(--color-text-muted);
  opacity: 0.8;
}

.tree-header {
  padding: 12px 12px 8px 12px;
}

/* Recreate mode styles */
.recreate-section {
  padding: 8px 12px;
}

.recreate-trigger {
  display: flex;
}

/* Link-style trigger button */
.recreate-button {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: var(--color-text-muted);
  width: 100%;
  justify-content: flex-start;
  background: none !important;
  box-shadow: none !important;
  transform: none !important;
  padding: 4px 0 !important;
}

.recreate-button:hover {
  color: var(--color-accent);
  text-decoration: underline;
  background: none !important;
  box-shadow: none !important;
  transform: none !important;
}

.recreate-controls {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.recreate-header {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  font-weight: 500;
}

.recreate-model-select {
  font-size: 0.75rem;
  padding: 4px 6px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
}

.recreate-actions {
  display: flex;
  gap: 8px;
}

/* Button-style action buttons */
.recreate-actions button {
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.cancel-btn {
  background: rgba(0, 0, 0, 0.05);
  color: var(--color-text-secondary);
}

.cancel-btn:hover {
  background: rgba(0, 0, 0, 0.1);
}

.proceed-btn {
  background: var(--color-accent);
  color: white;
}

.proceed-btn:hover:not(:disabled) {
  background: var(--color-accent-dark, #4a90e2);
}

.proceed-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.recreate-item {
  cursor: pointer;
}

.recreate-item:hover {
  background: rgba(0, 0, 0, 0.03) !important;
}

.recreate-checkbox {
  width: 14px;
  height: 14px;
  cursor: pointer;
  accent-color: var(--color-accent);
}

.stop-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  color: white;
  cursor: pointer;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.stop-button:hover {
  background: rgba(255, 255, 255, 0.5);
}

.upsell-cta {
  flex-shrink: 0;
  padding: 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: linear-gradient(135deg, #f0f4ff 0%, #f8f0ff 100%);
}

.upsell-headline {
  margin: 0 0 6px 0;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.upsell-body {
  margin: 0 0 6px 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--color-text-secondary);
}

.upsell-button {
  display: block;
  text-align: center;
  padding: 8px 12px;
  background: var(--color-primary, #6366f1);
  color: white;
  border-radius: var(--border-radius, 6px);
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  transition: opacity 0.15s;
}

.upsell-button:hover {
  opacity: 0.9;
}

.upsell-single {
  display: block;
  text-align: center;
  margin-top: 8px;
  font-size: 12px;
  color: var(--color-text-muted);
  text-decoration: none;
}

.upsell-single:hover {
  color: var(--color-text-secondary);
  text-decoration: underline;
}
</style>
